import { processEnergyDataWithExtrapolation, ExtrapolationResult } from '../../services/energyExtrapolationService';
import { supabaseClient } from '../../services/supabase';

export interface EnergySeriesResult {
  chartData: any;
  chartOptions: any;
  latestYear?: number;
  latestValue?: number;
}

type Sector = 'residential' | 'commercial';

// Sector-specific color shades (dark=observed, medium=interpolated, light=extrapolated)
const sectorColors: Record<Sector, { observed: string; interpolated: string; extrapolated: string }> = {
  residential: {
    observed: '#1e40af',      // Dark blue
    interpolated: '#3b82f6',  // Medium blue
    extrapolated: '#60a5fa'   // Light blue
  },
  commercial: {
    observed: '#166534',      // Dark green
    interpolated: '#22c55e',  // Medium green
    extrapolated: '#4ade80'   // Light green
  }
};

const commonOptions = (title: string, yLabel: string, stacked = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { 
      position: 'bottom',
      labels: {
        padding: 10,
        usePointStyle: true,
        pointStyle: 'rect'
      }
    },
    title: { display: true, text: title },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const v = Number(ctx.parsed.y);
          return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
        }
      }
    }
  },
  scales: {
    x: { stacked, title: { display: true, text: 'Year' } },
    y: { stacked, beginAtZero: true, title: { display: true, text: yLabel }, ticks: { callback: (v: any) => Number(v).toLocaleString() } }
  },
  layout: {
    padding: {
      bottom: 20
    }
  }
});

export async function fetchElectricitySeries(location: string, sector: Sector): Promise<EnergySeriesResult> {
  // Use the new format tables (elec_res_format, elec_com_format)
  const table = sector === 'residential' ? 'elec_res_format' : 'elec_com_format';
  const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
  
  try {
    // Get geofips for the location
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('geofips, location_name, geography_type')
      .eq('location_name', location)
      .limit(1)
      .single();

    if (locationError || !locationData) {
      console.error('Error fetching location geofips:', locationError);
      throw new Error(`Could not find location: ${location}`);
    }

    const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
    
    // Fetch data from new format table
    const { data: energyData, error: energyError } = await supabaseClient
      .from(table)
      .select('year, total_kwh, measurement, method_note')
      .eq('geofips', rawGeofips)
      .eq('sector', sector === 'residential' ? 'Residential' : 'Commercial')
      .in('year', years)
      .order('year', { ascending: true });

    if (energyError) {
      console.error('Error fetching energy data:', energyError);
      throw energyError;
    }

    // Process data into observed, interpolated, extrapolated arrays
    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];
    
    const dataMap = new Map<number, { value: number; measurement: string }>();
    
    (energyData || []).forEach((row: any) => {
      const year = row.year;
      const value = parseFloat(row.total_kwh);
      
      if (!isNaN(value) && value >= 0) {
        let measurement = (row.measurement || 'observed').toLowerCase().trim();
        const methodNote = (row.method_note || '').toLowerCase().trim();
        
        // Check method_note for extrapolation/interpolation
        if (methodNote.includes('extrapol')) {
          measurement = 'estimated';
        } else if (methodNote.includes('interpol')) {
          measurement = 'interpolated';
        }
        
        if (!dataMap.has(year)) {
          dataMap.set(year, { value, measurement });
        }
      }
    });
    
    // Build arrays for all years
    years.forEach(year => {
      const data = dataMap.get(year);
      if (data) {
        const measurement = data.measurement;
        if (measurement === 'observed') {
          observedData.push(data.value);
          interpolatedData.push(0);
          extrapolatedData.push(0);
        } else if (measurement === 'interpolated' || measurement.includes('interpol')) {
          observedData.push(0);
          interpolatedData.push(data.value);
          extrapolatedData.push(0);
        } else if (measurement === 'estimated' || measurement === 'extrapolated' || measurement.includes('extrapol')) {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(data.value);
        } else {
          // Unknown measurement type, treat as observed
          observedData.push(data.value);
          interpolatedData.push(0);
          extrapolatedData.push(0);
        }
      } else {
        // No data for this year
      observedData.push(0);
      interpolatedData.push(0);
      extrapolatedData.push(0);
      }
    });
    
    const chartData = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = observedData.length ? 
      (observedData[observedData.length - 1] + interpolatedData[interpolatedData.length - 1] + extrapolatedData[extrapolatedData.length - 1]) : 
      undefined;
    
    return {
      chartData,
      chartOptions: commonOptions(`${sector === 'residential' ? 'Residential' : 'Commercial'} Electricity Consumption (kWh)`, 'kWh', true),
      latestYear,
      latestValue
    };
    
  } catch (error) {
    console.error(`Error fetching electricity data for ${location}:`, error);
    throw error;
  }
}

export async function fetchElectricityStacked(location: string): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchElectricitySeries(location, 'residential'),
    fetchElectricitySeries(location, 'commercial')
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  // Create maps for each series
  const resMap = new Map((res.chartData.labels || []).map((y: number, i: number) => [y, (res.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));
  const comMap = new Map((com.chartData.labels || []).map((y: number, i: number) => [y, (com.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Electricity', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Electricity', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions('Total Electricity Consumption (kWh)', 'kWh', true)
  };
}

export async function fetchNaturalGasSeries(location: string, sector: Sector): Promise<EnergySeriesResult> {
  // Use the new format tables (ng_res_format, ng_com_format)
  const table = sector === 'residential' ? 'ng_res_format' : 'ng_com_format';
  const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
  
  try {
    // Get geofips for the location
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('geofips, location_name, geography_type')
      .eq('location_name', location)
      .limit(1)
      .single();

    if (locationError || !locationData) {
      console.error('Error fetching location geofips:', locationError);
      throw new Error(`Could not find location: ${location}`);
    }

    const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
    
    // Fetch data from new format table
    const { data: energyData, error: energyError } = await supabaseClient
      .from(table)
      .select('year, total_therms, measurement, method_note')
      .eq('geofips', rawGeofips)
      .eq('sector', sector === 'residential' ? 'Residential' : 'Commercial')
      .in('year', years)
      .order('year', { ascending: true });

    if (energyError) {
      console.error('Error fetching energy data:', energyError);
      throw energyError;
    }

    // Process data into observed, interpolated, extrapolated arrays
    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];
    
    const dataMap = new Map<number, { value: number; measurement: string }>();
    
    (energyData || []).forEach((row: any) => {
      const year = row.year;
      const value = parseFloat(row.total_therms) || 0;
      const measurement = row.measurement || '';
      const methodNote = row.method_note || '';
      
      // Determine source type based on measurement and method_note
      let source = 'observed';
      if (measurement.toLowerCase().includes('interpolated') || methodNote.toLowerCase().includes('interpol')) {
        source = 'interpolated';
      } else if (measurement.toLowerCase().includes('extrapolated') || measurement.toLowerCase().includes('estimated') || methodNote.toLowerCase().includes('extrapol')) {
        source = 'extrapolated';
      }
      
      // Keep the last value if duplicates exist
      if (!dataMap.has(year) || source === 'observed') {
        dataMap.set(year, { value, measurement: source });
      }
    });
    
    years.forEach((year) => {
      const entry = dataMap.get(year);
      const value = entry ? entry.value : 0;
      const source = entry ? entry.measurement : 'extrapolated';
      
      // Initialize all arrays with zeros for this year
      observedData.push(0);
      interpolatedData.push(0);
      extrapolatedData.push(0);
      
      // Put the actual value in the appropriate dataset
      if (source === 'observed') {
        observedData[observedData.length - 1] = value;
      } else if (source === 'interpolated') {
        interpolatedData[interpolatedData.length - 1] = value;
      } else {
        extrapolatedData[extrapolatedData.length - 1] = value;
      }
    });
    
    const chartDataGas = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = observedData.length ? 
      (observedData[observedData.length - 1] || interpolatedData[interpolatedData.length - 1] || extrapolatedData[extrapolatedData.length - 1] || 0) : 
      undefined;
    
    return {
      chartData: chartDataGas,
      chartOptions: commonOptions(`${sector === 'residential' ? 'Residential' : 'Commercial'} Natural Gas (therms)`, 'Therms', true),
      latestYear,
      latestValue
    };
    
  } catch (error) {
    console.error(`Error fetching natural gas data for ${location}:`, error);
    throw error;
  }
}

export async function fetchNaturalGasStacked(location: string): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchNaturalGasSeries(location, 'residential'),
    fetchNaturalGasSeries(location, 'commercial')
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  // Create maps for each series
  const resMap = new Map((res.chartData.labels || []).map((y: number, i: number) => [y, (res.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));
  const comMap = new Map((com.chartData.labels || []).map((y: number, i: number) => [y, (com.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Natural Gas', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Natural Gas', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions('Total Natural Gas (therms)', 'Therms', true)
  };
}


